import { useMemo } from 'react';
import { TravelMapBlockType } from '../Accommodations.types';
import { SearchParams, useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import type { LatLngLiteral } from '@shared/core/google-maps/types';
import { useHotelBookingPartnerLink } from '@shared/hooks/useHotelBookingPartnerLink';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useJoyPlaceIdBySourceIdQuery } from '@graphql/generated';
import { isValidUuId } from '@shared/utils/uuid';
import { isValidBookingUrl } from '@shared/utils/accommodations';

/**
 * Arguments for hotel information used in redirection logic.
 */
interface HotelArgs
  extends Readonly<{
    type?: TravelMapBlockType;
    id?: string | null;
    itemId?: string;
    joyPlaceId?: string | null;
    address?: string | null;
    googlePlaceId?: string | null;
    url?: string | null;
    websiteUrl?: string | null;
    location?: LatLngLiteral;
    zhId?: string | null;
    displayName?: string | null;
    reservedRoomBlocks?: {
      roomBlockId: string;
    };
    accommodationRoomBlock?: {
      bookingUrl?: string | null;
      isJoyManagedRoomBlock?: boolean | null;
    } | null;
  }> {}

/**
 * Validates if an accommodation place ID is valid for routing purposes.
 *
 * @param id - The ID to validate
 * @param zhId - The zhId to exclude from validation
 * @returns true if ID is a valid UUID and not the zhId
 */
const isValidAccommodationPlaceId = (id?: Maybe<string>, zhId?: Maybe<string>): boolean => {
  return !!id && id !== zhId && isValidUuId(id);
};

/**
 * Determines the best accommodation place ID from available options.
 *
 * @param itemId - Primary accommodation place ID (preferred)
 * @param id - Fallback accommodation place ID
 * @param zhId - zhId to exclude from validation
 * @param joyPlaceId - Joy place ID to exclude from validation
 * @returns Valid accommodation place ID or null
 */
const getAccommodationPlaceId = (itemId: Maybe<string>, id: Maybe<string>, zhId: Maybe<string>, joyPlaceId: Maybe<string>): string | null => {
  if (isValidAccommodationPlaceId(itemId, zhId) && itemId !== joyPlaceId) return itemId!;
  if (isValidAccommodationPlaceId(id, zhId) && id !== joyPlaceId) return id!;
  return null;
};

/**
 * Hook to generate a redirect URL for a hotel based on hotel type, data availability, and context.
 *
 * This hook determines the appropriate redirect URL by checking multiple conditions in priority order:
 * 1. Roomblocks: If hotel is added as a roomblock (by CX or Joy-managed), uses roomblock booking path
 * 2. Accommodation Place: If valid accommodation place ID exists, uses accommodation place route
 * 3. Joy Place: If joyPlaceId is available (directly or resolved from zhId), uses new hotel PDP route
 * 4. Fallback: Generates hotel partner link using available hotel data (address, Google Place ID, website, etc.)
 *
 * The hook automatically resolves joyPlaceId from zhId when needed using a GraphQL query, ensuring
 * proper routing even when the place ID isn't directly available.
 *
 * @param {Object} params - Parameters object
 * @param {HotelArgs} params.hotel - Hotel data used for redirection logic
 * @param {TravelMapBlockType} [params.hotel.type] - Type of travel map block (determines routing priority)
 * @param {string} [params.hotel.id] - Hotel/accommodation ID (used as fallback if itemId invalid)
 * @param {string} [params.hotel.itemId] - Primary accommodation place ID (preferred over id)
 * @param {string} [params.hotel.joyPlaceId] - Direct Joy place ID (avoids need for GraphQL lookup)
 * @param {string} [params.hotel.zhId] - Zillow/ZH ID used to resolve joyPlaceId via GraphQL if not provided
 * @param {string} [params.hotel.address] - Hotel address for fallback URL generation
 * @param {string} [params.hotel.googlePlaceId] - Google Places ID for fallback URL generation
 * @param {string} [params.hotel.url] - Hotel URL (fallback for websiteUrl)
 * @param {string} [params.hotel.websiteUrl] - Hotel website URL (preferred for fallback)
 * @param {LatLngLiteral} [params.hotel.location] - Hotel geographic coordinates for fallback URL
 * @param {string} [params.hotel.displayName] - Hotel name for fallback URL generation
 * @param {Object} [params.hotel.accommodationRoomBlock] - Roomblock configuration
 * @param {string} [params.hotel.accommodationRoomBlock.bookingUrl] - Required booking URL for roomblock routing
 * @param {boolean} [params.hotel.accommodationRoomBlock.isJoyManagedRoomBlock] - Flag indicating Joy-managed roomblock
 * @param {SearchParams} params.searchParams - Query parameters to append to the URL (e.g., telemetry/UTM params, dates, guests)
 *
 * @returns {string | undefined} The generated redirect URL, or undefined if:
 *   - Event handle is not available
 *   - Data is still loading (event info or joy place lookup)
 *   - Roomblock type without booking URL
 *   - No valid identifiers or hotel data available
 *
 */
export const useHotelRedirectUrl = ({ hotel, searchParams }: { hotel: HotelArgs; searchParams: SearchParams }): string | undefined => {
  const { eventHandle, eventInfo, loading: loadingEvent } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const hotelRoutePaths = useHotelRoutePaths();
  const hotelBookingPartnerLink = useHotelBookingPartnerLink();

  // Determine if we need to query for joyPlaceId
  const shouldQueryJoyPlace = Boolean(eventId && hotel.zhId && !hotel.joyPlaceId);

  const { data, loading: loadingJoyPlace } = useJoyPlaceIdBySourceIdQuery({
    batchMode: 'fast',
    variables: {
      payload: {
        eventId: eventId!,
        sourceId: hotel.zhId!,
        sourceType: 'zh',
      },
    },
    skip: !shouldQueryJoyPlace,
  });

  // Memoize loading state for clarity
  const loading = useMemo(() => loadingEvent || loadingJoyPlace, [loadingEvent, loadingJoyPlace]);
  const resolvedJoyPlaceId = hotel.joyPlaceId || data?.joyPlaceBySourceId?.id;

  // Memoize accommodation place ID calculation
  // @todo - Cleanup with ticket - TRAV-2162 - Update useHotelRedirectUrl to get accommodation place id by isCouplePick remove unwanted calculations.
  const accommodationPlaceId = useMemo(() => getAccommodationPlaceId(hotel.itemId, hotel.id, hotel.zhId, hotel.joyPlaceId), [hotel]);

  // Memoize roomblock routing check
  const isRoomblock = useMemo(
    () => hotel.type === TravelMapBlockType.travelMapAccommodationsRoomblock || !!hotel.accommodationRoomBlock || !!hotel.reservedRoomBlocks?.roomBlockId,
    [hotel.type, hotel.accommodationRoomBlock, hotel.reservedRoomBlocks?.roomBlockId],
  );

  // Memoize partner link parameters to avoid inline object creation
  const partnerLinkParams = useMemo(
    () => ({
      address: hotel.address,
      googleId: hotel.googlePlaceId,
      hotelWebsite: hotel.websiteUrl || hotel.url,
      location: {
        lat: hotel.location?.lat || 0,
        lng: hotel.location?.lng || 0,
      },
      name: hotel.displayName,
      zhId: hotel.zhId,
      ignoreAvailability: true,
    }),
    [hotel],
  );

  // Calculate hotel redirect URL using useMemo instead of useState + useEffect
  const hotelRedirectUrl = useMemo(() => {
    // Early return if prerequisites not met
    if (!eventHandle || loading) {
      return undefined;
    }

    // Priority 1: Roomblock routing
    const roomBlockId = hotel.id || hotel.reservedRoomBlocks?.roomBlockId;
    if (isRoomblock && roomBlockId) {
      // Roomblock requires booking URL to be present
      const bookingUrl = hotel.accommodationRoomBlock?.bookingUrl || (hotel.reservedRoomBlocks?.roomBlockId && hotel.websiteUrl ? hotel.websiteUrl : undefined);
      if (!bookingUrl || !isValidBookingUrl(bookingUrl)) {
        return undefined;
      }
      return hotelRoutePaths.roomblock.buildRelativeUrl(eventHandle, roomBlockId, searchParams);
    }

    // Priority 2: Accommodation place routing
    if (accommodationPlaceId) {
      return hotelRoutePaths.accommodationPlace.buildRelativeUrl(eventHandle, accommodationPlaceId, searchParams);
    }

    // Priority 3: Joy Place routing
    if (resolvedJoyPlaceId) {
      return hotelRoutePaths.hotelDetails.buildRelativeUrl(eventHandle, resolvedJoyPlaceId, searchParams);
    }

    // Priority 4: Fallback to generated partner link
    const generatedHotelUrl = hotelBookingPartnerLink.generateHotelUrl(partnerLinkParams);
    if (generatedHotelUrl) {
      return `/${eventHandle}${generatedHotelUrl.pathname + generatedHotelUrl.search}`;
    }

    return undefined;
  }, [
    hotel,
    eventHandle,
    resolvedJoyPlaceId,
    accommodationPlaceId,
    hotelRoutePaths.roomblock,
    hotelRoutePaths.hotelDetails,
    hotelRoutePaths.accommodationPlace,
    hotelBookingPartnerLink,
    loading,
    searchParams,
    isRoomblock,
    partnerLinkParams,
  ]);

  return hotelRedirectUrl;
};
